import { useEffect, useState } from "react";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { TosModal, PrivacyModal } from "../components/LegalModals";
import ContactModal from "../components/ContactModal";
import ChromeExtensionPanel from "../components/ChromeExtensionPanel";
import AdminReports from "../components/AdminReports";
import { Search, Sparkles, Plus, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

const STATE_STYLE = {
  trial: { label: "Trial", className: "bg-teal/15 text-teal border-teal/40" },
  nag: { label: "Nag", className: "bg-orange-alert/15 text-orange-alert border-orange-alert/40" },
  hard_locked: { label: "Hard Locked", className: "bg-red-critical/15 text-red-critical border-red-critical/40" },
  payment_locked: { label: "Payment Locked", className: "bg-red-critical/15 text-red-critical border-red-critical/40" },
  active: { label: "Active Premium", className: "bg-teal/25 text-white border-teal" },
  lifetime: { label: "Lifetime", className: "bg-white/10 text-white border-white/40" },
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [tos, setTos] = useState(false);
  const [pri, setPri] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [chromeOpen, setChromeOpen] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const [{ data: a }, { data: u }] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/users", { params: q ? { search: q } : {} }),
      ]);
      setAnalytics(a);
      setUsers(u.users);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const extend = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/extend-trial`);
      toast.success("Trial extended by 7 days");
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const grant = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/grant-lifetime`);
      toast.success("Lifetime access granted");
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const lock = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/lock`);
      toast.success("Account locked");
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const sendReport = async () => {
    try {
      await api.post("/admin/send-weekly-report");
      toast.success("Weekly report generated and stored in historical_reports.");
      setReportsRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);

  return (
    <div className="min-h-screen flex bg-obsidian text-white">
      <Sidebar onOpenTos={() => setTos(true)} onOpenPrivacy={() => setPri(true)} onOpenContact={() => setContactOpen(true)} onOpenChrome={() => setChromeOpen(true)} />

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="border-b border-border bg-charcoal/40 backdrop-blur-md">
          <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">Admin · Privacy Shielded</div>
              <h1 className="font-display text-2xl text-white tracking-tight">User Management</h1>
            </div>
            <Button
              data-testid="send-weekly-report-btn"
              onClick={sendReport}
              variant="outline"
              className="border-border bg-charcoal hover:bg-white/[0.04] text-white rounded-sm"
            >
              <Mail className="h-4 w-4 mr-2 text-teal" /> Run Weekly Report
            </Button>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-6 space-y-6">
          {/* Analytics tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-analytics">
            {analytics && [
              { k: "Total Users", v: analytics.total_users },
              { k: "New / 7d", v: analytics.new_signups_7d },
              { k: "Active Trials", v: analytics.active_trials },
              { k: "Conversions", v: analytics.total_conversions },
              { k: "MRR", v: `$${analytics.mrr.toFixed(2)}` },
            ].map((t) => (
              <div key={t.k} className="sr-card p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">{t.k}</div>
                <div className="mt-2 text-2xl font-display font-black text-white">{t.v}</div>
              </div>
            ))}
          </div>

          {/* Search bar */}
          <form onSubmit={onSearch} className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name…"
                className="pl-9 bg-charcoal border-border rounded-sm focus-visible:ring-teal"
              />
            </div>
            <Button data-testid="admin-search-btn" type="submit" variant="outline" className="border-border bg-charcoal rounded-sm">Search</Button>
            {search && (
              <Button type="button" variant="ghost" onClick={() => { setSearch(""); load(""); }} className="text-white/60 hover:text-white">Clear</Button>
            )}
          </form>

          {/* Users table */}
          <div className="sr-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em]">User</TableHead>
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em]">Registered</TableHead>
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em]">Last Login</TableHead>
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em]">Status</TableHead>
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em]">Gens</TableHead>
                  <TableHead className="text-white/50 font-mono text-[10px] uppercase tracking-[0.16em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-users-table">
                {loading && (
                  <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-white/40 py-8 font-mono text-xs sr-blink">Loading…</TableCell></TableRow>
                )}
                {!loading && users.length === 0 && (
                  <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-white/40 py-8">No users found.</TableCell></TableRow>
                )}
                {users.map((u) => {
                  const s = STATE_STYLE[u.funnel_state] || STATE_STYLE.trial;
                  return (
                    <TableRow key={u.user_id} className="border-border hover:bg-white/[0.02]">
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{u.name}</span>
                          <span className="text-white/40 text-xs font-mono">{u.email}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-white/70 text-xs font-mono">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-white/70 text-xs font-mono">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-sm font-mono text-[10px] tracking-wider ${s.className}`}>
                          {s.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white/70 text-sm font-mono">{u.total_generations}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            data-testid={`extend-trial-${u.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => extend(u.user_id)}
                            className="h-7 border-border bg-charcoal hover:bg-teal/15 hover:border-teal/40 hover:text-teal text-white/70 rounded-sm text-[11px]"
                          >
                            <Plus className="h-3 w-3 mr-1" /> Extend
                          </Button>
                          <Button
                            data-testid={`grant-lifetime-${u.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => grant(u.user_id)}
                            className="h-7 border-border bg-charcoal hover:bg-teal/15 hover:border-teal/40 hover:text-teal text-white/70 rounded-sm text-[11px]"
                          >
                            <Sparkles className="h-3 w-3 mr-1" /> Lifetime
                          </Button>
                          <Button
                            data-testid={`lock-user-${u.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => lock(u.user_id)}
                            className="h-7 border-border bg-charcoal hover:bg-red-critical/15 hover:border-red-critical/40 hover:text-red-critical text-white/70 rounded-sm text-[11px]"
                          >
                            <Lock className="h-3 w-3 mr-1" /> Lock
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/30 pt-2">
            Privacy: admin queries cannot access user-pasted content or AI outputs — those fields are never stored.
          </div>

          {/* Historical Reports archive */}
          <div className="sr-divider my-2" />
          <AdminReports key={reportsRefreshKey} onRunNow={sendReport} />
        </div>
      </main>

      <TosModal open={tos} onOpenChange={setTos} />
      <PrivacyModal open={pri} onOpenChange={setPri} />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
      <ChromeExtensionPanel open={chromeOpen} onOpenChange={setChromeOpen} />
    </div>
  );
}
